import Logo from "../components/Logo";
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  Truck,
  Warehouse,
  Globe,
  Clock,
  ArrowRight,
  CheckCircle2,
  Package,
  Map,
  BadgeAlert,
  ArrowUpRight,
  X,
  Activity,
} from "lucide-react";
import TrackingWidget from "../components/TrackingWidget";
import InteractiveMap from "../components/InteractiveMap";

export default function Home({
  shipments,
  trackedShipment,
  setTrackedShipment,
  onTabChange,
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [activeService, setActiveService] = useState(null);
  const [feedbackMsg, setFeedbackMsg] = useState("");

  // Auto-scroll to tracking details when loaded
  useEffect(() => {
    if (trackedShipment) {
      setTimeout(() => {
        document
          .getElementById("jpsy-tracking-view")
          ?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  }, [trackedShipment?.awb]);

  const handleSearch = (type, value) => {
    setIsLoading(true);
    setFeedbackMsg("");

    setTimeout(() => {
      setIsLoading(false);
      let found;

      if (type === "mobile") {
        // Look up by mobile numbers (either sender or receiver)
        found = shipments.find(
          (s) => s.senderMobile === value || s.receiverMobile === value,
        );
      } else if (type === "awb") {
        found = shipments.find(
          (s) => s.awb.toLowerCase() === value.toLowerCase(),
        );
      } else if (type === "orderId") {
        found = shipments.find(
          (s) => s.orderId.toLowerCase() === value.toLowerCase(),
        );
      } else if (type === "lrn") {
        found = shipments.find(
          (s) => s.lrn.toLowerCase() === value.toLowerCase(),
        );
      }

      if (found) {
        setTrackedShipment(found);
      } else {
        setFeedbackMsg(
          `No matching active shipments found for this ${type}. Feel free to try search value: "JPSY10012026".`,
        );
        setTrackedShipment(null);
      }
    }, 800); // realistic delay animation
  };

  const services = [
    {
      id: "express",
      name: "Express Parcel",
      icon: <Package className="w-6 h-6" />,
      tagline: "Last-mile courier network delivering within 24-48 hours.",
      desc: "Our flagship express courier service operates 24/7 cross-belt automated sorters, handling over 2.5 million packages daily with real-time OTP confirmation at delivery.",
    },
    {
      id: "warehousing",
      name: "Warehousing",
      icon: <Warehouse className="w-6 h-6" />,
      tagline: "Multi-tenant fulfillment gates with automated picking queues.",
      desc: "Strategically located fulfillment centers in major metropolitan corridors allow same-day dispatch, direct-to-retail distribution, and integrated e-commerce stocks.",
    },
    {
      id: "part-truckload",
      name: "Part Truckload",
      icon: <Truck className="w-6 h-6" />,
      tagline: "Cost-efficient less-than-truckload (LTL) solutions.",
      desc: "Extensive pan-India cargo consolidation utilizing state-of-the-art longhaul trucks. Pay only for the space your package takes with computerized volume scaling.",
    },
    {
      id: "full-truckload",
      name: "Full Truckload",
      icon: <Truck className="w-6 h-6 text-blue-500" />,
      tagline: "Dedicated door-to-door point container networks.",
      desc: "For high-volume manufacturing, JPSY provides fully sealed dedicated container fleets equipped with digital electronic locks and continuous geofenced GPS mapping.",
    },
    {
      id: "international",
      name: "International",
      icon: <Globe className="w-6 h-6" />,
      tagline: "Cross-border express airway Supply Chain & customs help.",
      desc: "Air charter connections to 220+ global countries. Handles express air freight sorting, customs brokerage paper clearance, and onward local destination courier delivery.",
    },
    {
      id: "jpsy-maps",
      name: "JPSY Maps (Beta)",
      icon: <Map className="w-6 h-6 text-emerald-500" />,
      tagline: "Address cleansing API and pinpoint route geofences.",
      desc: "Our proprietary location engine cleans, validates, and geocodes unstructured shipping addresses. Powered by spatial ML to predict exact last-mile delivery windows.",
    },
  ];

  return (
    <div className="flex-1 bg-slate-50 font-sans">
      {/* HERO SECTION - Deep carbon slate theme matching Delhivery layout */}
      <section className="relative min-h-[550px] bg-[#111215] flex items-center overflow-hidden py-16 px-4">
        {/* Visual background decor representing airway and land routes */}
        <div className="absolute inset-0 z-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#21185f]/10 rounded-full filter blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-slate-900/20 rounded-full filter blur-3xl" />
          {/* Subtle line grids */}
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#1f2937_1px,transparent_1px),linear-gradient(to_bottom,#1f2937_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)] opacity-30" />
        </div>

        <div className="max-w-7xl mx-auto w-full grid grid-cols-1 lg:grid-cols-12 gap-12 items-center relative z-10">
          {/* Left Text Column: Delhivery Style Jet Backdrop Branding */}
          <div className="lg:col-span-7 text-white space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 bg-[#21185f]/70 border border-[#263f98]/50 px-3.5 py-1.5 rounded-full text-xs text-[#93a4ff] font-semibold"
            >
              <Activity className="w-4 h-4 text-[#263f98] animate-pulse" />
              <span>India's Pioneer Tech-First Supply Chain Network</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-none text-white font-sans"
            >
              ON THE MOVE FOR{" "}
              <Logo className="inline-block h-auto w-48 sm:w-60 max-w-full align-middle" />
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-lg sm:text-xl font-medium text-slate-300 max-w-2xl leading-relaxed"
            >
              Taking local Indian pincodes to global cargo destinations. Over{" "}
              <span className="text-white font-semibold underline decoration-[#263f98] decoration-2">
                5 Billion+ shipments
              </span>{" "}
              delivered with integrated software sorters.
            </motion.p>

            {/* Quick trust metrics */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="grid grid-cols-3 gap-4 pt-4 border-t border-slate-900 max-w-lg text-slate-300 text-xs font-mono"
            >
              <div>
                <p className="text-lg font-black text-white font-sans">
                  18,500+
                </p>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider mt-0.5">
                  Pincodes
                </p>
              </div>
              <div>
                <p className="text-lg font-black text-white font-sans">24 hr</p>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider mt-0.5">
                  Local Corridor
                </p>
              </div>
              <div>
                <p className="text-lg font-black text-white font-sans">99.8%</p>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider mt-0.5">
                  SLA On-Time
                </p>
              </div>
            </motion.div>
          </div>

          {/* Right Column: Tracking Search Card */}
          <div className="lg:col-span-5 flex justify-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              className="w-full"
            >
              <TrackingWidget onSearch={handleSearch} isLoading={isLoading} />

              {feedbackMsg && (
                <div className="mt-3 bg-rose-950/40 border border-rose-900/40 rounded-xl p-3 text-xs text-rose-300 flex items-start gap-2.5">
                  <BadgeAlert className="w-4 h-4 text-rose-500 shrink-0 mt-0.5" />
                  <span>{feedbackMsg}</span>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* DYNAMIC LIVE TRACKING VIEWER SECTION (Revealed only when tracking is active) */}
      <AnimatePresence>
        {trackedShipment && (
          <motion.section
            id="jpsy-tracking-view"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="border-b border-slate-200 bg-white overflow-hidden scroll-mt-24"
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
              {/* Active Item Title Header */}
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 pb-6 border-b border-slate-100">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs bg-blue-100 text-blue-800 font-bold px-2.5 py-0.5 rounded font-mono">
                      AWB: {trackedShipment.awb}
                    </span>
                    <span className="text-xs bg-slate-100 text-slate-800 font-bold px-2.5 py-0.5 rounded font-mono">
                      Order ID: {trackedShipment.orderId}
                    </span>
                  </div>
                  <h2 className="text-2xl font-sans font-black text-slate-900 tracking-tight mt-1.5 flex items-center gap-2">
                    <span>Shipment Track & Trace</span>
                  </h2>
                </div>

                <div className="flex items-center gap-3 w-full md:w-auto">
                  <button
                    onClick={() => {
                      onTabChange("admin");
                      // Focus Ops updating
                    }}
                    className="flex-1 md:flex-none border border-rose-200 hover:bg-rose-50 text-rose-700 font-bold text-xs px-4 py-2 rounded-xl transition-colors flex items-center justify-center gap-1.5"
                  >
                    <span className="w-2 h-2 rounded-full bg-rose-500 animate-ping" />
                    Simulate Updates
                  </button>

                  <button
                    onClick={() => setTrackedShipment(null)}
                    className="p-2 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors"
                    title="Close Tracking"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Grid: Left Column Tracker Milestones, Right Column JPSY Live Map */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-8">
                {/* Milestones Left Box (7 Columns) */}
                <div className="lg:col-span-6 space-y-6">
                  <div className="bg-slate-50 border border-slate-200/60 rounded-xl p-5 shadow-sm space-y-4">
                    <h3 className="font-sans font-bold text-sm text-slate-900 uppercase tracking-wider border-b border-slate-200/50 pb-2 flex items-center gap-2">
                      <Clock className="w-4 h-4 text-blue-600" />
                      Delivery Milestones
                    </h3>

                    {/* Progress indicators */}
                    <div className="relative pl-6 space-y-6 border-l-2 border-slate-200">
                      {trackedShipment.milestones.map((ms, index) => {
                        const isCurrent =
                          trackedShipment.status === ms.status ||
                          (index === 0 && trackedShipment.status === "booked");
                        return (
                          <div
                            key={`${ms.status}-${index}`}
                            className="relative"
                          >
                            {/* Pin dot */}
                            <span
                              className={`absolute -left-[31px] top-1 w-4 h-4 rounded-full border-2 flex items-center justify-center z-10 ${
                                ms.completed
                                  ? "bg-emerald-500 border-emerald-500 text-white"
                                  : "bg-white border-slate-300 text-slate-300"
                              }`}
                            >
                              {ms.completed && (
                                <CheckCircle2 className="w-3 h-3 text-white" />
                              )}
                            </span>

                            {/* Milestone textual detail */}
                            <div>
                              <div className="flex justify-between items-start gap-4">
                                <h4
                                  className={`text-sm font-bold font-sans ${
                                    isCurrent
                                      ? "text-blue-600"
                                      : ms.completed
                                        ? "text-slate-800"
                                        : "text-slate-400"
                                  }`}
                                >
                                  {ms.title}
                                </h4>
                                <span className="font-mono text-[10px] text-slate-500 shrink-0 font-semibold">
                                  {ms.timestamp}
                                </span>
                              </div>
                              <p className="text-xs text-slate-400 font-mono mt-0.5">
                                {ms.location}
                              </p>
                              <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                                {ms.description}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Parcel Specifications */}
                  <div className="border border-slate-200 rounded-xl p-5 bg-white grid grid-cols-2 sm:grid-cols-3 gap-4 text-xs font-sans">
                    <div>
                      <p className="text-[10px] uppercase font-mono text-slate-400 tracking-wider">
                        Service Selected
                      </p>
                      <p className="font-bold text-slate-800 mt-0.5">
                        {trackedShipment.serviceType}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase font-mono text-slate-400 tracking-wider">
                        Gross Weight
                      </p>
                      <p className="font-bold text-slate-800 mt-0.5">
                        {trackedShipment.weight} kg
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase font-mono text-slate-400 tracking-wider">
                        Dimensions
                      </p>
                      <p className="font-bold text-slate-800 mt-0.5">
                        {trackedShipment.dimensions}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase font-mono text-slate-400 tracking-wider">
                        Origin
                      </p>
                      <p className="font-bold text-slate-800 mt-0.5">
                        {trackedShipment.originCity} (
                        {trackedShipment.originPincode})
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase font-mono text-slate-400 tracking-wider">
                        Destination
                      </p>
                      <p className="font-bold text-slate-800 mt-0.5">
                        {trackedShipment.destinationCity} (
                        {trackedShipment.destinationPincode})
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase font-mono text-slate-400 tracking-wider">
                        Payment Mode
                      </p>
                      <p className="font-bold text-slate-800 mt-0.5">
                        {trackedShipment.paymentType}{" "}
                        {trackedShipment.codAmount > 0
                          ? `(₹${trackedShipment.codAmount})`
                          : ""}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Interactive Map Column (5 Columns) */}
                <div className="lg:col-span-6">
                  <InteractiveMap shipment={trackedShipment} />
                </div>
              </div>
            </div>
          </motion.section>
        )}
      </AnimatePresence>

      {/* CORE SERVICES OVERVIEW */}
      <section id="jpsy-services-section" className="py-20 bg-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section title */}
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <h2 className="text-3xl sm:text-4xl font-sans font-black tracking-tight text-slate-900 leading-tight">
              Integrated Supply Chain & Supply Chain Suites
            </h2>
            <p className="text-base text-slate-500">
              JPSY coordinates road linehaul trucking, express parcel hubs,
              automated sorting sorters, and cross-border airway forwarding
              networks.
            </p>
          </div>

          {/* Service grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((svc) => (
              <motion.div
                key={svc.id}
                whileHover={{ y: -4 }}
                onClick={() =>
                  setActiveService(activeService === svc.id ? null : svc.id)
                }
                className={`bg-white border p-6 rounded-2xl cursor-pointer transition-all duration-300 relative overflow-hidden ${
                  activeService === svc.id
                    ? "border-blue-500 ring-2 ring-blue-500/20 shadow-lg"
                    : "border-slate-200/70 hover:border-slate-300 hover:shadow-md"
                }`}
              >
                <div className="flex items-center gap-4">
                  <div
                    className={`p-3 rounded-xl ${activeService === svc.id ? "bg-blue-600 text-white" : "bg-blue-50 text-blue-600"}`}
                  >
                    {svc.icon}
                  </div>
                  <div>
                    <h3 className="font-sans font-bold text-slate-900">
                      {svc.name}
                    </h3>
                    <p className="text-[11px] text-slate-400 font-mono">
                      Standard SLA Guaranteed
                    </p>
                  </div>
                </div>

                <p className="text-xs text-slate-500 mt-4 leading-relaxed font-sans font-medium">
                  {svc.tagline}
                </p>

                {/* Expanded text */}
                <AnimatePresence>
                  {activeService === svc.id && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="mt-4 pt-4 border-t border-slate-100 text-xs text-slate-400 leading-relaxed"
                    >
                      {svc.desc}
                    </motion.div>
                  )}
                </AnimatePresence>

                <div className="flex items-center gap-1.5 text-xs text-blue-600 font-bold mt-4 pt-4 border-t border-slate-50">
                  <span>
                    {activeService === svc.id ? "Hide Details" : "View Scope"}
                  </span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* VALUE PROP & MERCHANT SIGNUP CALL TO ACTION */}
      <section className="py-20 bg-slate-950 text-white relative overflow-hidden">
        {/* Abstract grids */}
        <div className="absolute inset-0 bg-[radial-gradient(#1e1b4b_1px,transparent_1px)] [background-size:24px_24px] opacity-20" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 space-y-6">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-sans font-black tracking-tight text-white leading-none">
              Are you an E-commerce Seller or Enterprise Merchant?
            </h2>
            <p className="text-base sm:text-lg text-slate-300 max-w-2xl leading-relaxed">
              Integrate your Shopify, WooCommerce, or Custom ERP to manage
              multi-pincode pickups, digital cash-on-delivery (COD) clearance,
              and real-time electronic airway billing automatically.
            </p>

            <div className="grid grid-cols-2 gap-4 max-w-lg text-xs font-semibold">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-blue-500" />
                <span>Zero Monthly Setup Fee</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-blue-500" />
                <span>Automated reverse pick-ups</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-blue-500" />
                <span>Immediate 24-hr COD Remittance</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-blue-500" />
                <span>Developer-friendly JSON APIs</span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-5 shadow-2xl">
            <div className="space-y-1">
              <h3 className="font-sans font-bold text-lg text-white">
                Create Merchant Account
              </h3>
              <p className="text-xs text-slate-400">
                Unlock express parcel booking, digital wallet, and pin
                calculators immediately.
              </p>
            </div>

            <div className="space-y-4 text-xs">
              <div className="flex flex-col gap-1.5">
                <label className="text-[10px] uppercase font-mono tracking-wider font-semibold text-slate-400">
                  Company Name
                </label>
                <input
                  type="text"
                  placeholder="e.g., Apex Retail Corp"
                  className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3.5 py-2.5 text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-[10px] uppercase font-mono tracking-wider font-semibold text-slate-400">
                  Business GSTIN (Optional)
                </label>
                <input
                  type="text"
                  placeholder="e.g., 29AAAAA1111A1Z1"
                  className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3.5 py-2.5 text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
            </div>

            <button
              onClick={() => onTabChange("dashboard")}
              className="w-full bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white text-xs font-bold py-3 px-4 rounded-xl transition-colors shadow flex items-center justify-center gap-2 cursor-pointer"
            >
              <span>Access Merchant Panel</span>
              <ArrowUpRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
